// VerifyEmailPage — the link in the sign-up email (v2.08).
//   /verify-email?id=<bot>&token=<token>
// ⛔ NEVER verify on load. Mail scanners (e.g. Outlook Safe Links) open every link in an email on their own;
// a page that verified on load would use up the token before the person ever saw it. The token is sent
// only when the person presses OK in the confirmation dialog (X and Cancel go to sign-in without verifying);
// a verified account then opens the Sign In page. See verifyEmail in src/utils/botFactoryAPI.tsx.
// Unverified accounts can still sign in, so nothing here blocks anyone.
import React, { useMemo, useState } from 'react';
import { verifyEmail, VerifyEmailResult } from '../utils/botFactoryAPI';
import { usePreAuthOrg } from '../utils/usePreAuthOrg';
import { PreAuthNotice } from '../components/header/PreAuthNotice';
import { ConfirmDialog } from '../components/ConfirmDialog';

type Phase = 'ready' | VerifyEmailResult;

const FAILURE_TEXT: Record<Exclude<VerifyEmailResult, 'verified'>, string> = {
  expired: 'This link has expired.',
  invalid: "This link isn't valid.",
  unavailable: "Verification isn't available right now. Please try again later.",
};

function VerifyEmailPage() {
  const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const botId = queryParams.get('id') || '';
  const token = (queryParams.get('token') || '').trim();

  // Same branding source as the sign-in, sign-up and reset pages: the agent record from the bootstrap API.
  const pre = usePreAuthOrg(botId || null);
  const org: any = botId && pre.status === 'ready' ? pre.org : null;

  const pageBg = org?.containerStyle?.backgroundColor || '#0a2740';
  const pageFg = org?.bodyStyle?.color || org?.containerStyle?.color || '#e8eef5';
  const buttonBg = org?.buttonStyle?.backgroundColor || '#004c8c';
  const buttonFg = org?.buttonStyle?.color || '#ffffff';
  const logo = org?.logo || '/assets/app/logo192.png';
  const title = org?.name || org?.title || '';

  // A link with no token can't be verified: say so, and send nothing.
  const [phase, setPhase] = useState<Phase>(token ? 'ready' : 'invalid');
  const [busy, setBusy] = useState(false);

  // Keep the agent (?id=) so the sign-in page and its branding come back; never carry the token.
  const signInUrl = botId ? `/signin?id=${encodeURIComponent(botId)}` : '/signin';

  const handleVerify = async () => {
    if (!token || busy) return;
    setBusy(true);
    try {
      const result = await verifyEmail(token);
      if (result === 'verified') {
        window.location.assign(signInUrl); // verified (or already was): open Sign In for the first sign-in
        return;
      }
      setPhase(result);
    } finally {
      setBusy(false);
    }
  };

  // An id that does not resolve is a state, never a generic page for it (same rule as sign up).
  if (botId && pre.status !== 'ready' && pre.status !== 'loading') return <PreAuthNotice status={pre.status} />;

  const canTry = phase === 'ready' || phase === 'unavailable';

  return (
    <div id="pageContainer" className="verifyPage" style={{ backgroundColor: pageBg, color: pageFg }}>
      <style>{`
        html, body, #root { background-color: ${pageBg} !important; }
        .verifyPage { min-height: 100dvh; width: 100%; box-sizing: border-box; padding: 32px 16px 64px;
          display: flex; flex-direction: column; align-items: center; }
        .verifyPage * { box-sizing: border-box; }
        .verifyCard { width: 100%; max-width: 640px; text-align: center; }
        .verifyLogo { display: block; margin: 0 auto 12px; max-height: 80px; max-width: min(260px, 80%);
          width: auto; height: auto; object-fit: contain; }
        .verifyTitle { font-size: 1.25rem; font-weight: 700; margin: 0 0 6px; }
        .verifyButtons { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 8px; }
        .verifyButton { flex: 1 1 140px; padding: 10px 18px; border: none; border-radius: 24px;
          font-weight: 600; font-size: 16px; cursor: pointer; background: ${buttonBg}; color: ${buttonFg};
          text-align: center; text-decoration: none; }
        .verifyMessage { margin: 0 0 12px; padding: 10px 12px; border-radius: 6px; font-size: 0.95rem;
          background: #fff; color: #7a1c1c; text-align: left; }
      `}</style>

      <div className="verifyCard">
        <img className="verifyLogo" src={logo} alt={title ? `${title} logo` : 'logo'} />
        {title && <div className="verifyTitle">{title}</div>}

        <h1 className="verifyTitle">Verify your email</h1>
        {(phase === 'expired' || phase === 'invalid') && (
          <>
            <div className="verifyMessage" role="alert">
              {FAILURE_TEXT[phase]}
            </div>
            <div className="verifyButtons">
              <a className="verifyButton" href={signInUrl}>
                Sign in
              </a>
            </div>
          </>
        )}
      </div>

      {/* The confirmation: OK verifies and opens Sign In; X and Cancel open Sign In without verifying. */}
      <ConfirmDialog
        open={canTry}
        title="Verify your email"
        message="Press OK to confirm this is your email address."
        notice={phase === 'unavailable' ? FAILURE_TEXT.unavailable : undefined}
        busy={busy}
        onOk={handleVerify}
        onCancel={() => window.location.assign(signInUrl)}
        colors={{ background: pageBg, text: pageFg, buttonBackground: buttonBg, buttonText: buttonFg }}
      />
    </div>
  );
}

export default VerifyEmailPage;
